import { ActiveStatusCodes } from '@/common/constants/status';
import {
    Project,
    ProjectWithDetail,
    ProjectWithLeader,
    ProjectWithTeam,
} from '@/common/types/databaseTypes';
import { authService } from '@/features/auth/services/authService';
import { Database, supabase } from '@tickitacka/shared';

export type ProjectInsert = Database['public']['Tables']['project']['Insert'];
export type CreateProjectParams = Omit<
    ProjectInsert,
    'id' | 'register_date' | 'update_date' | 'enable'
> & {
    participants?: string[]; // 참여자 ID 배열 (선택적)
};

export const projectService = {
    async getProjectsByTeamId(
        teamId: string,
        offset = 0,
        limit = 30
    ): Promise<ProjectWithLeader[]> {
        try {
            // 1. project_sharing 테이블에서 팀에 공유된 project_id 목록만 조회
            const { data: sharedProjectIds, error: sharingError } =
                await supabase
                    .from('project_sharing')
                    .select('project_id')
                    .eq('team_id', teamId)
                    .eq('enable', true);

            if (sharingError) throw sharingError;

            if (!sharedProjectIds || sharedProjectIds.length === 0) {
                return [];
            }

            // 2. 해당 팀의 모든 활성 프로젝트 조회 (페이징 적용)
            const { data, error } = await supabase
                .from('project')
                .select(
                    `
          *,
          leader:user!leader_id (
            id,
            name,
            profile_image_url
          )
        `
                )
                .in(
                    'id',
                    sharedProjectIds.map(p => p.project_id)
                )
                .eq('enable', true)
                .order('register_date', { ascending: false })
                .range(offset, offset + limit - 1);

            if (error) throw error;

            return data;
        } catch (error) {
            console.error(`Failed to fetch project ${teamId}`, error);
            throw error;
        }
    },

    async getProjectDetailsById(projectId: string): Promise<ProjectWithDetail> {
        try {
            const { data, error } = await supabase
                .from('project')
                .select(
                    `
                    *,
                    team:team_id (
                        id,
                        name,
                        profile_image_url
                    ),
                    leader:user!leader_id (
                        id,
                        name,
                        profile_image_url
                    ),
                    shared_teams:project_sharing!project_id (
                        team!team_id (
                        id,
                        name,
                        profile_image_url
                        )
                    ),
                    participants:project_participant!project_id (
                        user!user_id (
                        id,
                        name,
                        profile_image_url
                        )
                    )
                `
                )
                .eq('id', projectId)
                .eq('enable', true)
                .filter('shared_teams.enable', 'eq', true)
                .single();

            if (error) throw error;

            return {
                ...data,
                shared_teams: data.shared_teams?.map(s => s.team) ?? [],
                participants: data.participants?.map(p => p.user) ?? [],
            };
        } catch (error) {
            console.error(`Failed to fetch project detail ${projectId}`, error);
            throw error;
        }
    },

    async getProjectById(projectId: string): Promise<Project> {
        try {
            const { data, error } = await supabase
                .from('project')
                .select('*')
                .eq('id', projectId)
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error(`Failed to fetch project ${projectId}`, error);
            throw error;
        }
    },

    async getSharedProjects(
        // goalId: string,
        teamId: string
    ): Promise<{ projects: ProjectWithTeam[]; sharedProjectIds: string[] }> {
        try {
            // 1. 해당 목표를 공유하는 팀 ID 목록 가져오기
            // const { data: goalSharingData, error: goalSharingError } =
            //     await supabase
            //         .from('goal_sharing')
            //         .select('team_id')
            //         .eq('goal_id', goalId)
            //         .eq('enable', true);

            // if (goalSharingError) throw goalSharingError;
            // if (!goalSharingData?.length)
            // return { projects: [], sharedProjectIds: [] };

            // 팀 ID 배열 추출
            // const goalSharedTeamIds = goalSharingData.map(
            //     share => share.team_id
            // );

            // 2. 팀이 공유받고 있는 프로젝트 ID 목록 가져오기
            let sharedProjectIds: string[] = [];
            if (teamId) {
                const { data: projectSharingData, error: projectSharingError } =
                    await supabase
                        .from('project_sharing')
                        .select('project_id')
                        .eq('team_id', teamId)
                        .eq('enable', true);

                if (projectSharingError) throw projectSharingError;
                sharedProjectIds =
                    projectSharingData?.map(share => share.project_id) || [];
            }

            // 3. OR 조건 구성 (팀 소속 프로젝트 또는 공유받은 프로젝트)
            // let orCondition = `team_id.in.(${goalSharedTeamIds.join(',')})`;
            // if (sharedProjectIds.length > 0) {
            // orCondition += `,id.in.(${sharedProjectIds.join(',')})`;
            // }

            // 4. 해당 조건에 맞는 모든 활성 프로젝트 가져오기
            const { data: projects, error: projectsError } = await supabase
                .from('project')
                .select(
                    `
                    *,
                    team:team_id (
                        id,
                        name,
                        profile_image_url
                    )
                `
                )
                // .or(orCondition)
                .in('id', sharedProjectIds)
                .eq('enable', true);

            if (projectsError) throw projectsError;
            if (!projects?.length)
                return { projects: [], sharedProjectIds: [] };

            // 5. 중복 제거 (ID 기준)
            return {
                projects: projects.filter(
                    (project, index) =>
                        projects.findIndex(p => p.id === project.id) === index
                ),
                sharedProjectIds: sharedProjectIds,
            };
        } catch (error) {
            console.error(
                `팀 공유 프로젝트 조회 실패 (팀 ID: ${teamId}):`,
                error
            );
            if (error instanceof Error) throw error;
            throw new Error(
                '목표를 공유받은 팀들의 프로젝트 조회 중 오류가 발생했습니다.'
            );
        }
    },

    async createProject(newProject: CreateProjectParams): Promise<Project> {
        try {
            // participants와 나머지 프로젝트 데이터 분리
            const { participants, ...projectData } = newProject;

            // 1. 프로젝트 생성
            const { data, error } = await supabase
                .from('project')
                .insert({
                    id: crypto.randomUUID(),
                    ...projectData,
                })
                .select()
                .single();

            if (error) throw error;

            // 2. 참여자가 있다면 추가
            if (participants && participants.length > 0) {
                const participantPromises = participants.map(async userId => {
                    const { error } = await supabase
                        .from('project_participant')
                        .upsert(
                            {
                                project_id: data.id,
                                user_id: userId,
                                register_id: projectData.register_id,
                                register_date: new Date().toISOString(),
                                update_id: projectData.register_id,
                                update_date: new Date().toISOString(),
                            },
                            { onConflict: 'project_id,user_id' }
                        );

                    if (error) throw error;
                });

                try {
                    await Promise.all(participantPromises);
                } catch (participantError) {
                    // 참여자 추가 실패 시 생성된 프로젝트 삭제 (롤백)
                    console.warn(
                        '참여자 추가 실패, 프로젝트 롤백:',
                        participantError
                    );
                    await supabase.from('project').delete().eq('id', data.id);
                    throw new Error('프로젝트 참여자 추가에 실패했습니다.');
                }
            }

            return data;
        } catch (error) {
            console.error('Failed to create project', error);
            throw error;
        }
    },

    async updateProject(
        projectId: string,
        fields: Partial<Project>
    ): Promise<Project> {
        const user = await authService.getCurrentUser();
        if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

        try {
            const { data, error } = await supabase
                .from('project')
                .update({
                    ...fields,
                    update_date: new Date().toISOString(),
                    update_id: user.id,
                })
                .eq('id', projectId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Failed to update project', error);
            throw error;
        }
    },

    async deleteProject(projectId: string): Promise<void> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { error } = await supabase
                .from('project')
                .update({
                    enable: false,
                    update_date: new Date().toISOString(),
                    update_id: user.id,
                })
                .eq('id', projectId);

            if (error) throw error;
        } catch (error) {
            console.error(`Failed to delete project ${projectId}`, error);
            throw error;
        }
    },

    async updateProjectSharing(
        projectId: string,
        teamId: string,
        enable: boolean
    ): Promise<void> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            // 이미 공유 기록이 있는지 확인
            const { data: existingShare } = await supabase
                .from('project_sharing')
                .select('*')
                .eq('project_id', projectId)
                .eq('team_id', teamId)
                .maybeSingle();

            // 현재 요청된 상태와 기존 상태 비교
            if (existingShare) {
                // 상태가 같으면 아무 작업도 하지 않음
                if (existingShare.enable === enable) return;

                // 상태가 다르면 업데이트
                const { error } = await supabase
                    .from('project_sharing')
                    .update({
                        enable: enable,
                        update_date: new Date().toISOString(),
                        update_id: user.id,
                    })
                    .eq('id', existingShare.id);

                if (error) throw error;
            }
            // 공유 기록이 없고 공유 요청인 경우에만 새로 생성
            else if (enable) {
                const { error } = await supabase
                    .from('project_sharing')
                    .insert({
                        project_id: projectId,
                        team_id: teamId,
                        register_id: user.id,
                        enable: true,
                        is_owner: false,
                    });

                if (error) throw error;
            }
            // 공유 기록이 없고 공유 취소 요청인 경우는 아무 작업 안함
        } catch (error) {
            console.error(
                `Failed to update project sharing for project ${projectId} with team ${teamId}`,
                error
            );
            throw error;
        }
    },

    /**
     * 프로젝트가 특정 팀에 공유되고 있는지 확인하는 함수
     * @param projectId 프로젝트 ID
     * @param teamId 팀 ID
     * @returns 공유 여부 (boolean)
     */
    async checkProjectSharingStatus(
        projectId: string,
        teamId: string
    ): Promise<boolean> {
        try {
            if (!projectId || !teamId) return false;

            const { data } = await supabase
                .from('project_sharing')
                .select('*')
                .eq('project_id', projectId)
                .eq('team_id', teamId)
                .eq('enable', true)
                .maybeSingle();

            return !!data; // 데이터가 있으면 true, 없으면 false 반환
        } catch (error) {
            console.error(
                `Failed to check project sharing status for project ${projectId} with team ${teamId}`,
                error
            );
            return false;
        }
    },

    /**
     * 사용자가 참여하고 있는 프로젝트 목록 조회
     * @param userId 사용자 ID
     * @returns 프로젝트 목록
     */
    async getMyProjects(
        userId: string,
        { workspaceId }: { workspaceId?: string } = {}
    ): Promise<Project[]> {
        try {
            // 1단계: 사용자가 참여한 프로젝트 ID들
            const { data: participations } = await supabase
                .from('project_participant')
                .select('project_id')
                .eq('user_id', userId)
                .eq('enable', true);

            if (!participations || participations.length === 0) {
                return [];
            }

            // 2단계: 해당 프로젝트들 중 workspaceId에 해당하는, Active 상태인 것만
            let query = supabase
                .from('project')
                .select('*, team:team_id (id, workspace_id)')
                .in(
                    'id',
                    participations.map(p => p.project_id)
                )
                .in('status_code', ActiveStatusCodes)
                .eq('enable', true);

            if (workspaceId) {
                query = query.eq('team.workspace_id', workspaceId);
            }

            const { data, error } = await query.order('register_date', {
                ascending: false,
            });

            if (error) throw error;

            // team이 null이 아닌 프로젝트만 필터링
            const validProjects =
                data?.filter(project => project.team !== null) || [];
            return validProjects;
        } catch (error) {
            console.error(
                `Failed to fetch user's projects for user ${userId}`,
                error
            );
            throw error;
        }
    },

    // 프로젝트 참여자 관련 함수
    async getProjectParticipantsByProjectId(projectId: string) {
        try {
            const { data, error } = await supabase
                .from('project_participant')
                .select('user_id')
                .eq('project_id', projectId);

            if (error) throw error;

            // 중복없는 배열 리턴
            return Array.from(new Set(data.map(item => item.user_id)));
        } catch (error) {
            console.error('Failed to fetch project participants', error);
            throw error;
        }
    },

    async addProjectParticipant(
        projectId: string,
        userId: string,
        registerId: string
    ): Promise<boolean> {
        try {
            const { error } = await supabase.from('project_participant').upsert(
                {
                    project_id: projectId,
                    user_id: userId,
                    register_id: registerId,
                    register_date: new Date().toISOString(),
                    update_id: registerId,
                    update_date: new Date().toISOString(),
                },
                { onConflict: 'project_id,user_id' }
            );

            if (error) throw error;
            return true;
        } catch (error) {
            console.error('Failed to add project participant', error);
            return false;
        }
    },

    async removeProjectParticipant(
        projectId: string,
        userId: string
    ): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('project_participant')
                .delete()
                .match({
                    project_id: projectId,
                    user_id: userId,
                });

            if (error) throw error;
            return true;
        } catch (error) {
            console.error('Failed to remove project participant', error);
            return false;
        }
    },

    /**
     * 프로젝트 참여자를 제거하고 종속 관계도 함께 처리
     * 1. project_participant에서 제거
     * 2. 해당 프로젝트의 모든 goal에서 goal_participant 제거
     * 3. 해당 유저의 todo 중 프로젝트 goal과 연결된 것들의 goal_id를 null로 업데이트
     */
    async removeProjectParticipantWithDependencies(
        projectId: string,
        userId: string,
        registerId: string
    ): Promise<boolean> {
        try {
            // 1. project_participant에서 제거
            const { error: projectParticipantError } = await supabase
                .from('project_participant')
                .delete()
                .match({
                    project_id: projectId,
                    user_id: userId,
                });

            if (projectParticipantError) throw projectParticipantError;

            // 2. 해당 프로젝트에 속한 모든 goal 조회
            const { data: projectGoals, error: goalsError } = await supabase
                .from('goal')
                .select('id')
                .eq('project_id', projectId)
                .eq('enable', true);

            if (goalsError) throw goalsError;

            if (projectGoals && projectGoals.length > 0) {
                const goalIds = projectGoals.map(goal => goal.id);

                // 3. 해당 goal들에서 goal_participant 제거
                const { error: goalParticipantError } = await supabase
                    .from('goal_participant')
                    .delete()
                    .eq('user_id', userId)
                    .in('goal_id', goalIds);

                if (goalParticipantError) throw goalParticipantError;

                // 4. 해당 유저의 todo 중 프로젝트 goal과 연결된 것들의 goal_id를 null로 업데이트
                const { error: todoUpdateError } = await supabase
                    .from('todo')
                    .update({
                        goal_id: null,
                        update_date: new Date().toISOString(),
                        update_id: registerId,
                    })
                    .eq('user_id', userId)
                    .in('goal_id', goalIds)
                    .eq('enable', true);

                if (todoUpdateError) throw todoUpdateError;
            }

            return true;
        } catch (error) {
            console.error(
                'Failed to remove project participant with dependencies',
                error
            );
            return false;
        }
    },
};
